import _ from "lodash";
import env from "../constants/env";
import * as awsS3 from "../persistence/s3";
import { getOrgId } from "./orgsActions";
import { listActivityWorkflowWorkItems, updateActivityWorkflowWorkItemProgress, updateActivityWorkflowWorkItemStatus } from "./notificationActions";
import { showWarningToast } from "./alertActions";
import { getCustomHomeProjectTemplate, getProjectOpsFromWorkItem, normalizeProjectOpsContractAnchor } from "../utils/projectOpsUtils";
import { isIchOrg } from "../utils/ichMilestones";
import {
	applyIchContractMilestones,
	buildIchDraftAddendumProto,
	buildIchLockedContractProto,
	getNewlyReachedIchMilestones,
	isIchAddendumStage,
	planIchProjectAdvance
} from "../utils/ichProjectOpsAutomation";

/**
 * ICH (Infinity Custom Homes) automation that runs on the save paths of the
 * platform: stamp provable milestones on the house's contract anchor, then move
 * the Project Ops work items that were bound to those milestones. Every entry
 * point is a no-op for other organizations.
 */

const WORK_ITEM_LIST_LIMIT = 500;

export function isIchAutomationEnabled(orgId = getOrgId()) {
	return isIchOrg(orgId);
}

export function getIchProjectIdFromInstance(instance = {}) {
	return _.toString(
		_.get(instance, "projectOpsProjectId")
		|| _.get(instance, ["projectOps", "projectId"])
		|| _.get(instance, ["projectContractAnchor", "projectId"])
		|| ""
	).trim();
}

/**
 * Returns { instance, reachedMilestoneKeys }. The instance comes back untouched
 * outside the ICH org, so no other customer's save path changes shape.
 *
 * context: { submitted?, activityId?, activityName?, at? } describing the save.
 */
export function stampIchInstanceForSave(instance, context = {}) {
	if (!_.isPlainObject(instance) || !isIchAutomationEnabled()) {
		return { instance, reachedMilestoneKeys: [] };
	}
	const next = applyIchContractMilestones(instance, context);
	return {
		instance: next,
		reachedMilestoneKeys: getNewlyReachedIchMilestones(instance, next)
	};
}

function getWorkItemsFromResponse(response) {
	return _.get(response, ["body", "workItems"])
		|| _.get(response, "workItems")
		|| _.get(response, ["body", "items"])
		|| _.get(response, "items")
		|| [];
}

/**
 * Opens a blocked project stage from the ICH Homes dashboard: the deliberate
 * "send the next selection to the customer" step. Mirrors what the Work Queue
 * does before opening a stage: the work item first inherits the anchor's locked
 * pricing contract and the anchor itself, so the session launches with the
 * contract context. If that inheritance is refused (for example the current
 * user is not the assignee) the stage is still opened, like the Work Queue.
 */
/**
 * The anchor reference a contributor stage must carry. The anchor instance's own
 * projectContractAnchor is built at launch, before its S3 key exists, so it can
 * lack instanceUrl; the exit sync that merges a contributor back into the home
 * refuses an anchor without it. Fill it from the instance and the known key.
 */
export function buildIchStageContractAnchor(anchorInstance = null, anchorInstanceUrl = "") {
	if (!_.isPlainObject(anchorInstance)) {
		return null;
	}
	const existing = _.isPlainObject(anchorInstance.projectContractAnchor) ? anchorInstance.projectContractAnchor : {};
	const projectOps = _.isPlainObject(anchorInstance.projectOps) ? anchorInstance.projectOps : {};
	const anchor = normalizeProjectOpsContractAnchor({
		...existing,
		projectId: existing.projectId || projectOps.projectId || anchorInstance.projectOpsProjectId || "",
		projectName: existing.projectName || projectOps.projectName || anchorInstance.projectOpsProjectName || "",
		activityId: existing.activityId || anchorInstance.id || "",
		activityInstanceId: existing.activityInstanceId || anchorInstance.instanceId || "",
		instanceUrl: existing.instanceUrl || _.toString(anchorInstanceUrl || "").trim(),
		stageKey: existing.stageKey || projectOps.stageKey || anchorInstance.projectOpsStageKey || "",
		stageLabel: existing.stageLabel || projectOps.stageLabel || anchorInstance.projectOpsStageLabel || "",
		source: existing.source || "ich_homes_dashboard_anchor"
	});
	return anchor && (anchor.workItemId || anchor.instanceUrl || anchor.activityInstanceId) ? anchor : null;
}

/**
 * The manual step Jeff used to do in the pricer after locking the sale: draft
 * Addendum #N on the home's contract so the next selection session prices into
 * it. Saves the home (which re-indexes it) and syncs the anchor work item, the
 * same way a pricing save does. Returns the proto the contributor should inherit.
 */
export async function draftIchAddendumOnAnchor(anchorInstance = null, anchorInstanceUrl = "", options = {}) {
	const currentProto = anchorInstance && _.isPlainObject(anchorInstance.pricingContractProto) ? anchorInstance.pricingContractProto : null;
	const draft = buildIchDraftAddendumProto(currentProto);
	if (!draft) {
		return { instance: anchorInstance, proto: currentProto, document: null, created: false, saved: false };
	}
	if (!draft.changed) {
		return { instance: anchorInstance, proto: draft.proto, document: draft.document, created: false, saved: false };
	}
	const url = _.toString(anchorInstanceUrl || "").trim();
	const nextInstance = stampIchInstanceForSave({ ...anchorInstance, pricingContractProto: draft.proto }).instance;
	if (!url) {
		console.warn("ICH addendum drafted for the stage only; the home has no instance url to save to");
		return { instance: nextInstance, proto: draft.proto, document: draft.document, created: draft.created, saved: false };
	}
	await awsS3.saveInstance(env.s3.contentBucket, url, nextInstance);
	await syncIchAnchorWorkItem(nextInstance, url, {
		reason: _.toString(options.reason || `${draft.document.title} drafted from the ICH Homes dashboard.`),
		source: "ichHomesDashboardDraftAddendum"
	});
	return { instance: nextInstance, proto: draft.proto, document: draft.document, created: draft.created, saved: true };
}

/**
 * Mirrors a home save onto its anchor work item the way a pricing save does, so
 * the Work Queue and My Activities see the same contract. Never throws.
 */
async function syncIchAnchorWorkItem(instance, url, options = {}) {
	const anchorRef = buildIchStageContractAnchor(instance, url);
	if (!anchorRef || !anchorRef.workItemId) {
		return false;
	}
	const pricingContractProto = _.isPlainObject(instance.pricingContractProto) ? instance.pricingContractProto : null;
	try {
		await updateActivityWorkflowWorkItemProgress(anchorRef.workItemId, {
			reason: _.toString(options.reason || "Home updated from the ICH Homes dashboard."),
			activityId: anchorRef.activityId || null,
			activityInstanceId: anchorRef.activityInstanceId || null,
			instanceUrl: url,
			pricingContractProto,
			projectOps: instance.projectOps || null,
			projectContractAnchor: anchorRef,
			activityOps: {
				source: options.source || "ichHomesDashboard",
				projectOps: instance.projectOps || null,
				pricingContractProto,
				projectContractAnchor: anchorRef
			}
		});
		return true;
	} catch (err) {
		console.warn("ICH home saved, but the anchor work item was not updated", {
			workItemId: anchorRef.workItemId,
			error: err && err.message ? err.message : err
		});
		return false;
	}
}

/**
 * Locks the Agreement of Sale from the dashboard: freezes the home's priced
 * items as the locked Initial Purchase, stamps Contract Date from the lock
 * time, saves the home (re-indexing it) and syncs the anchor work item. Returns
 * null when there is nothing to lock. The caller advances the project stages.
 */
export async function lockIchAgreementOfSale(anchorInstance = null, anchorInstanceUrl = "", options = {}) {
	const url = _.toString(anchorInstanceUrl || "").trim();
	if (!_.isPlainObject(anchorInstance) || !url) {
		throw new Error("The home must be loaded before its agreement can be locked.");
	}
	const locked = buildIchLockedContractProto(anchorInstance, options);
	if (!locked) {
		return null;
	}
	const withContract = { ...anchorInstance, pricingContractProto: locked.proto };
	const nextInstance = applyIchContractMilestones(withContract);
	const reachedMilestoneKeys = getNewlyReachedIchMilestones(anchorInstance, nextInstance);
	await awsS3.saveInstance(env.s3.contentBucket, url, nextInstance);
	const synced = await syncIchAnchorWorkItem(nextInstance, url, {
		reason: "Agreement of Sale locked from the ICH Homes dashboard.",
		source: "ichHomesDashboardLockAgreement"
	});
	return { instance: nextInstance, document: locked.document, created: locked.created, reachedMilestoneKeys, synced };
}

export async function startIchProjectStage(workItem = {}, anchorInstance = null, options = {}) {
	if (!workItem || !workItem.id) {
		throw new Error("A work item is required to start a stage.");
	}
	const reason = _.toString(options.reason || "Stage started from the ICH Homes dashboard.");
	let pricingContractProto = anchorInstance && _.isPlainObject(anchorInstance.pricingContractProto) ? anchorInstance.pricingContractProto : null;
	let addendum = null;
	if (pricingContractProto && isIchAddendumStage(workItem)) {
		addendum = await draftIchAddendumOnAnchor(anchorInstance, options.anchorInstanceUrl, { reason });
		if (addendum.proto) {
			pricingContractProto = addendum.proto;
		}
	}
	const projectContractAnchor = buildIchStageContractAnchor(addendum && addendum.instance ? addendum.instance : anchorInstance, options.anchorInstanceUrl);
	let inheritedContext = false;
	if (pricingContractProto || projectContractAnchor) {
		const rawProjectOps = getProjectOpsFromWorkItem(workItem);
		const projectOps = projectContractAnchor ? { ...rawProjectOps, projectContractAnchor } : rawProjectOps;
		try {
			await updateActivityWorkflowWorkItemProgress(workItem.id, {
				pricingContractProto,
				projectContractAnchor,
				projectOps,
				reason: `${reason} Inherited the project contract context.`,
				updatedFrom: "ichHomesDashboardStartStage",
				activityOps: {
					source: "ichHomesDashboardStartStage",
					projectOps,
					pricingContractProto,
					projectContractAnchor
				}
			});
			inheritedContext = true;
		} catch (err) {
			console.warn("ICH stage start could not inherit the contract context; opening anyway", {
				workItemId: workItem.id,
				error: err && err.message ? err.message : err
			});
		}
	}
	const result = await updateActivityWorkflowWorkItemStatus(workItem.id, "open", reason);
	return {
		result,
		inheritedContext,
		addendum: addendum && addendum.document
			? { id: addendum.document.id, title: addendum.document.title, created: addendum.created, saved: addendum.saved }
			: null
	};
}

/**
 * Completes the work items whose stage is bound to a milestone that was just
 * reached. Failures never block the save that triggered this; they are logged
 * and surfaced as a warning toast.
 */
export async function advanceIchProjectForMilestones(instance = {}, reachedMilestoneKeys = [], options = {}) {
	const projectId = _.toString(options.projectId || getIchProjectIdFromInstance(instance)).trim();
	if (!isIchAutomationEnabled() || !projectId || !Array.isArray(reachedMilestoneKeys) || reachedMilestoneKeys.length === 0) {
		return [];
	}

	let workItems;
	try {
		const response = await listActivityWorkflowWorkItems({ orgId: getOrgId(), limit: WORK_ITEM_LIST_LIMIT });
		workItems = getWorkItemsFromResponse(response);
	} catch (err) {
		console.warn("ICH project auto-advance could not list work items", {
			projectId,
			error: err && err.message ? err.message : err
		});
		showWarningToast("Saved, but the project stages could not be advanced automatically. Check the Work Queue.");
		return [];
	}

	const updates = planIchProjectAdvance(workItems, {
		projectId,
		template: options.template || getCustomHomeProjectTemplate(),
		reachedMilestoneKeys
	});

	const applied = [];
	for (let i = 0; i < updates.length; i += 1) {
		const update = updates[i];
		try {
			await updateActivityWorkflowWorkItemStatus(update.workItemId, update.status, update.reason);
			applied.push(update);
		} catch (err) {
			console.warn("ICH project auto-advance could not update a work item", {
				projectId,
				update,
				error: err && err.message ? err.message : err
			});
		}
	}
	return applied;
}
